/**
 * Hub Bozze Grafiche — riorganizzato per chiarezza operativa.
 *
 * Layout:
 *  - KPI card in alto (contatori per stato + evidenza "da validare")
 *  - Tab: [Da validare] [In corso] [Accettate] [Tutte]
 *  - Le righe "Da validare" hanno un puntino di notifica animato + CTA "Valida ora"
 *  - Detail modal: accetta un file / richiedi revisione
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useAgentAuth } from '../../contexts/AgentAuthContext';
import AgentLayout from './AgentLayout';
import {
  Palette, CheckCircle2, Loader2, X, RotateCcw, FileText, Download, ExternalLink,
  BellRing, Clock, Sparkles, ArrowRight, Image as ImageIcon, Plus,
} from 'lucide-react';
import GraphicRequestModal from './GraphicRequestModal';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABEL = {
  pending: { label: 'In attesa admin', color: 'bg-amber-100 text-amber-800', dotColor: '#f59e0b' },
  in_progress: { label: 'In lavorazione', color: 'bg-blue-100 text-blue-800', dotColor: '#2563eb' },
  files_ready: { label: 'Da validare', color: 'bg-fuchsia-100 text-fuchsia-800', dotColor: '#c026d3' },
  accepted: { label: 'Accettata', color: 'bg-green-100 text-green-800', dotColor: '#16a34a' },
  revision_requested: { label: 'Revisione in corso', color: 'bg-orange-100 text-orange-800', dotColor: '#ea580c' },
  rejected: { label: 'Rifiutata', color: 'bg-red-50 text-red-700', dotColor: '#dc2626' },
  cancelled: { label: 'Annullata', color: 'bg-gray-100 text-gray-600', dotColor: '#6b7280' },
  draft: { label: 'Bozza', color: 'bg-gray-100 text-gray-600', dotColor: '#6b7280' },
};

const isActionNeeded = (item) => item.status === 'files_ready';
const isInProgress = (item) => ['pending', 'in_progress', 'revision_requested'].includes(item.status);
const isDone = (item) => item.status === 'accepted';

const AgentGraphicRequests = () => {
  const { agentToken } = useAgentAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [revisionText, setRevisionText] = useState('');
  const [acting, setActing] = useState(false);
  const [tab, setTab] = useState('todo'); // todo | inprogress | done | all
  const [showPicker, setShowPicker] = useState(false);
  const [quotes, setQuotes] = useState([]);
  const [pickerQuoteId, setPickerQuoteId] = useState('');
  const [pickerItemIndex, setPickerItemIndex] = useState(0);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [newRequestModal, setNewRequestModal] = useState(null);

  const auth = () => ({ Authorization: `Bearer ${agentToken}` });

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/agent/graphic-requests`, { headers: auth() });
      if (r.ok) {
        const j = await r.json();
        setItems(j.data || []);
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { if (agentToken) load(); /* eslint-disable-next-line */ }, [agentToken]);

  const stats = useMemo(() => ({
    todo: items.filter(isActionNeeded).length,
    inprogress: items.filter(isInProgress).length,
    done: items.filter(isDone).length,
    all: items.length,
  }), [items]);

  const filtered = useMemo(() => {
    if (tab === 'todo') return items.filter(isActionNeeded);
    if (tab === 'inprogress') return items.filter(isInProgress);
    if (tab === 'done') return items.filter(isDone);
    return items;
  }, [items, tab]);

  const openDetail = async (item) => {
    try {
      const r = await fetch(`${API}/api/agent/graphic-requests/${item.id}`, { headers: auth() });
      if (r.ok) {
        const j = await r.json();
        setDetail(j.data);
        setRevisionText('');
      }
    } catch { /* ignore */ }
  };

  const acceptFile = async (fileId) => {
    if (!window.confirm('Accettare questo file come definitivo? Le altre proposte verranno archiviate.')) return;
    setActing(true);
    try {
      const r = await fetch(`${API}/api/agent/graphic-requests/${detail.id}/accept-file`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...auth() },
        body: JSON.stringify({ file_id: fileId }),
      });
      if (r.ok) { const j = await r.json(); setDetail(j.data); load(); }
    } finally { setActing(false); }
  };

  const requestRevision = async () => {
    if (!revisionText.trim()) return;
    setActing(true);
    try {
      const r = await fetch(`${API}/api/agent/graphic-requests/${detail.id}/request-revision`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...auth() },
        body: JSON.stringify({ revision_notes: revisionText.trim() }),
      });
      if (r.ok) { const j = await r.json(); setDetail(j.data); setRevisionText(''); load(); }
    } finally { setActing(false); }
  };

  const openPicker = async () => {
    setShowPicker(true);
    setPickerLoading(true);
    try {
      const r = await fetch(`${API}/api/agent/quotes`, { headers: auth() });
      if (r.ok) {
        const j = await r.json();
        // Solo preventivi non annullati/rifiutati
        const list = (j.data || []).filter((q) => !['rejected', 'expired', 'cancelled'].includes(q.status));
        setQuotes(list);
        if (list.length > 0) {
          setPickerQuoteId(list[0].id);
          setPickerItemIndex(0);
        }
      }
    } finally { setPickerLoading(false); }
  };

  const selectedQuote = useMemo(() => quotes.find((q) => q.id === pickerQuoteId), [quotes, pickerQuoteId]);
  const existingIndexes = useMemo(() => {
    if (!selectedQuote) return new Set();
    return new Set(
      items
        .filter((g) => g.quote_id === pickerQuoteId && !['cancelled', 'rejected'].includes(g.status))
        .map((g) => g.quote_item_index)
    );
  }, [items, selectedQuote, pickerQuoteId]);

  const confirmPicker = () => {
    if (!pickerQuoteId || selectedQuote == null) return;
    const item = (selectedQuote.items || [])[pickerItemIndex];
    if (!item) return;
    setNewRequestModal({
      quoteId: pickerQuoteId,
      itemIndex: pickerItemIndex,
      defaultProductName: `${item.description || 'Prodotto'} ${item.format || ''}`.trim(),
    });
    setShowPicker(false);
  };

  return (
    <AgentLayout>
      {/* Header */}
      <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Palette className="w-7 h-7 text-purple-600" /> Bozze Grafiche
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Centro di controllo per le richieste grafiche legate ai tuoi preventivi.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {stats.todo > 0 && (
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-fuchsia-500 to-purple-600 text-white shadow-lg" data-testid="gr-notify-banner">
              <BellRing className="w-4 h-4 animate-pulse" />
              <span className="text-sm font-semibold">
                {stats.todo === 1 ? '1 bozza aspetta la tua approvazione' : `${stats.todo} bozze aspettano la tua approvazione`}
              </span>
            </div>
          )}
          <button
            onClick={openPicker}
            data-testid="btn-new-graphic-request"
            className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm font-semibold shadow-md"
          >
            <Plus className="w-4 h-4" /> Nuova richiesta
          </button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <button
          onClick={() => setTab('todo')}
          data-testid="tab-kpi-todo"
          className={`text-left rounded-2xl p-4 border-2 transition-all ${tab === 'todo' ? 'border-fuchsia-500 shadow-lg' : 'border-transparent'} ${stats.todo > 0 ? 'bg-gradient-to-br from-fuchsia-50 to-purple-50 ring-1 ring-fuchsia-200' : 'bg-white ring-1 ring-gray-200'}`}
        >
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase font-semibold text-fuchsia-700">Da validare</div>
            {stats.todo > 0 && <span className="relative flex h-2.5 w-2.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-fuchsia-500 opacity-75" /><span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-fuchsia-600" /></span>}
          </div>
          <div className="mt-1 text-3xl font-bold text-fuchsia-900 tabular-nums">{stats.todo}</div>
          <div className="text-[11px] text-fuchsia-700 mt-1">nuovi file dall&apos;admin</div>
        </button>

        <button
          onClick={() => setTab('inprogress')}
          data-testid="tab-kpi-inprogress"
          className={`text-left rounded-2xl p-4 bg-white border-2 transition-all ${tab === 'inprogress' ? 'border-blue-500 shadow-md' : 'border-transparent ring-1 ring-gray-200'}`}
        >
          <div className="flex items-center gap-2 text-[10px] uppercase font-semibold text-blue-700">
            <Clock className="w-3 h-3" /> In corso
          </div>
          <div className="mt-1 text-3xl font-bold text-gray-900 tabular-nums">{stats.inprogress}</div>
          <div className="text-[11px] text-gray-500 mt-1">in lavorazione da studio</div>
        </button>

        <button
          onClick={() => setTab('done')}
          data-testid="tab-kpi-done"
          className={`text-left rounded-2xl p-4 bg-white border-2 transition-all ${tab === 'done' ? 'border-green-500 shadow-md' : 'border-transparent ring-1 ring-gray-200'}`}
        >
          <div className="flex items-center gap-2 text-[10px] uppercase font-semibold text-green-700">
            <CheckCircle2 className="w-3 h-3" /> Accettate
          </div>
          <div className="mt-1 text-3xl font-bold text-gray-900 tabular-nums">{stats.done}</div>
          <div className="text-[11px] text-gray-500 mt-1">pronte per la produzione</div>
        </button>

        <button
          onClick={() => setTab('all')}
          data-testid="tab-kpi-all"
          className={`text-left rounded-2xl p-4 bg-white border-2 transition-all ${tab === 'all' ? 'border-purple-500 shadow-md' : 'border-transparent ring-1 ring-gray-200'}`}
        >
          <div className="text-[10px] uppercase font-semibold text-purple-700">Tutte</div>
          <div className="mt-1 text-3xl font-bold text-gray-900 tabular-nums">{stats.all}</div>
          <div className="text-[11px] text-gray-500 mt-1">storico completo</div>
        </button>
      </div>

      {/* Onboarding hint */}
      <div className="mb-4 bg-purple-50 border border-purple-100 rounded-xl p-3 text-sm text-purple-900 flex items-start gap-2">
        <Sparkles className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <div>
          <b>Come funziona:</b> crea una richiesta grafica dal <a href="/agent/quotes" className="underline">preventivo</a> (bottone &quot;Richiedi bozza grafica&quot; su ogni riga prodotto).
          Poi qui puoi seguire lo stato: quando l&apos;admin carica un file, la card diventa <span className="text-fuchsia-700 font-semibold">fucsia con pallino animato</span>. Cliccala per validare o richiedere revisione.
        </div>
      </div>

      {/* Cards */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="py-16 text-center text-gray-500">Caricamento…</div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center text-gray-500">
            <Palette className="w-10 h-10 mx-auto text-gray-300 mb-2" />
            {tab === 'todo' && 'Nessuna bozza in attesa di approvazione. 🎉'}
            {tab === 'inprogress' && 'Nessuna bozza in corso.'}
            {tab === 'done' && 'Nessuna bozza ancora accettata.'}
            {tab === 'all' && 'Nessuna richiesta grafica. Creane una dai tuoi preventivi.'}
          </div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {filtered.map((it) => {
              const s = STATUS_LABEL[it.status] || { label: it.status, color: 'bg-gray-100' };
              const actionNeeded = isActionNeeded(it);
              return (
                <li
                  key={it.id}
                  className={`p-4 flex items-center gap-4 cursor-pointer transition-colors ${actionNeeded ? 'bg-gradient-to-r from-fuchsia-50/60 to-transparent hover:from-fuchsia-100/60' : 'hover:bg-purple-50/40'}`}
                  onClick={() => openDetail(it)}
                  data-testid={`gr-row-${it.id}`}
                >
                  {/* Left: dot + icon */}
                  <div className="flex-shrink-0 relative">
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${actionNeeded ? 'bg-fuchsia-100' : 'bg-purple-50'}`}>
                      <Palette className={`w-5 h-5 ${actionNeeded ? 'text-fuchsia-700' : 'text-purple-600'}`} />
                    </div>
                    {actionNeeded && (
                      <span className="absolute -top-1 -right-1 flex h-3 w-3">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-fuchsia-500 opacity-75" />
                        <span className="relative inline-flex rounded-full h-3 w-3 bg-fuchsia-600 ring-2 ring-white" />
                      </span>
                    )}
                  </div>

                  {/* Middle: info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-semibold text-gray-900 truncate">{it.product_name}</div>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${s.color}`}>{s.label}</span>
                      {(it.design_files || []).length > 0 && (
                        <span className="text-[10px] text-gray-500">📎 {it.design_files.length} file</span>
                      )}
                    </div>
                    <div className="text-xs text-gray-600 mt-0.5 flex items-center gap-2 flex-wrap">
                      <span>Prev. <span className="font-mono text-purple-700">#{it.quote_number || it.quote_id?.slice(-6)}</span></span>
                      {it.order_id && <span className="text-green-600 font-mono">→ ordine {it.order_id.slice(-6)}</span>}
                      {it.customer_name && <span>· {it.customer_name}</span>}
                    </div>
                    {(it.colors || []).length > 0 && (
                      <div className="flex gap-1 mt-1">
                        {it.colors.slice(0, 6).map((c, i) => {
                          const isHex = /^#([0-9A-F]{3}){1,2}$/i.test(c);
                          return isHex
                            ? <span key={i} className="w-3.5 h-3.5 rounded-full border border-gray-300 shadow-sm" style={{ backgroundColor: c }} title={c} />
                            : <span key={i} className="text-[9px] text-gray-500 bg-gray-100 rounded px-1 py-0.5">{c}</span>;
                        })}
                      </div>
                    )}
                  </div>

                  {/* Right: CTA */}
                  <div className="flex-shrink-0">
                    {actionNeeded ? (
                      <span className="inline-flex items-center gap-1 px-3 py-2 bg-fuchsia-600 text-white rounded-lg text-xs font-semibold shadow-md">
                        Valida ora <ArrowRight className="w-3 h-3" />
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">
                        {it.updated_at ? new Date(it.updated_at).toLocaleDateString('it-IT') : '—'}
                      </span>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Detail Modal */}
      {detail && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="gr-detail-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-fuchsia-500 text-white px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <div>
                <h2 className="text-lg font-semibold flex items-center gap-2"><Palette className="w-5 h-5" /> {detail.product_name}</h2>
                <div className="text-[11px] text-white/80 font-mono">{detail.id} · Prev. #{detail.quote_number}</div>
              </div>
              <button onClick={() => setDetail(null)} className="p-1 hover:bg-white/20 rounded"><X className="w-5 h-5" /></button>
            </div>

            <div className="p-6 space-y-4 text-sm">
              {/* Meta */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-[10px] uppercase text-gray-500 font-semibold">Cliente</div>
                  <div className="text-sm font-medium text-gray-900">{detail.customer_name || '—'}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-gray-500 font-semibold">Stato</div>
                  <span className={`inline-block mt-0.5 px-2 py-0.5 rounded-full text-[10px] font-semibold ${(STATUS_LABEL[detail.status] || {}).color || 'bg-gray-100'}`}>
                    {(STATUS_LABEL[detail.status] || {}).label || detail.status}
                  </span>
                </div>
              </div>

              {detail.colors?.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase text-gray-500 font-semibold">Colori</div>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {detail.colors.map((c, i) => {
                      const isHex = /^#([0-9A-F]{3}){1,2}$/i.test(c);
                      return (
                        <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-full text-xs">
                          {isHex && <span className="w-3 h-3 rounded-full border border-gray-300" style={{ backgroundColor: c }} />}
                          {c}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

              {detail.logo_url && (
                <div>
                  <div className="text-[10px] uppercase text-gray-500 font-semibold mb-1">Logo cliente</div>
                  <a href={detail.logo_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-purple-700 hover:underline text-xs">
                    <ImageIcon className="w-3 h-3" /> {detail.logo_original_name || 'Apri logo'}
                  </a>
                </div>
              )}

              {detail.notes && (
                <div>
                  <div className="text-[10px] uppercase text-gray-500 font-semibold">Note originali</div>
                  <div className="mt-1 text-sm text-gray-700 whitespace-pre-wrap bg-gray-50 rounded p-2">{detail.notes}</div>
                </div>
              )}

              {/* Files */}
              <div>
                <div className="text-[10px] uppercase text-gray-500 font-semibold mb-2">
                  Proposte grafiche ({(detail.design_files || []).length})
                </div>
                {(detail.design_files || []).length === 0 ? (
                  <div className="text-xs text-gray-500 italic border border-dashed border-gray-200 p-4 rounded text-center">
                    L&apos;admin non ha ancora caricato file. Riceverai una notifica quando saranno pronti.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {detail.design_files.map((f) => {
                      const isAccepted = detail.accepted_file_id === f.id;
                      return (
                        <div key={f.id} className={`flex items-center gap-2 border rounded-lg p-2 ${isAccepted ? 'border-green-300 bg-green-50' : 'border-gray-200'}`} data-testid={`gr-file-${f.id}`}>
                          <FileText className="w-4 h-4 text-purple-600 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-medium truncate">{f.original_name}</div>
                            <div className="text-[10px] text-gray-500">{Math.round((f.size_bytes || 0) / 1024)} KB · {new Date(f.uploaded_at).toLocaleString('it-IT')}</div>
                          </div>
                          {f.url && (
                            <a href={f.url} target="_blank" rel="noopener noreferrer" className="p-1.5 text-gray-500 hover:text-purple-700" title="Apri">
                              <Download className="w-4 h-4" />
                            </a>
                          )}
                          {isAccepted ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-600 text-white rounded text-xs font-semibold">
                              <CheckCircle2 className="w-3 h-3" /> Accettato
                            </span>
                          ) : detail.status !== 'accepted' ? (
                            <button onClick={() => acceptFile(f.id)} disabled={acting}
                              data-testid={`btn-accept-${f.id}`}
                              className="inline-flex items-center gap-1 px-2 py-1 bg-purple-600 text-white rounded text-xs hover:bg-purple-700 disabled:opacity-50">
                              <CheckCircle2 className="w-3 h-3" /> Accetta
                            </button>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {detail.status !== 'accepted' && (detail.design_files || []).length > 0 && (
                <div className="border-t pt-3">
                  <div className="text-[10px] uppercase text-gray-500 font-semibold">Richiedi revisione</div>
                  <textarea rows={2} value={revisionText} onChange={(e) => setRevisionText(e.target.value)}
                    placeholder="Es. cambiare font del titolo, spostare logo in basso, colore troppo scuro…"
                    data-testid="gr-revision-input"
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
                  <button onClick={requestRevision} disabled={acting || !revisionText.trim()}
                    data-testid="btn-request-revision"
                    className="mt-2 inline-flex items-center gap-1 px-3 py-1.5 bg-orange-100 text-orange-800 rounded-md text-xs hover:bg-orange-200 disabled:opacity-50">
                    <RotateCcw className="w-3 h-3" /> Invia richiesta revisione
                  </button>
                </div>
              )}

              {detail.revision_notes && (
                <div className="bg-orange-50 border border-orange-200 rounded p-2 text-xs">
                  <div className="font-semibold text-orange-800">Note revisione inviate:</div>
                  <div className="text-orange-700 whitespace-pre-wrap">{detail.revision_notes}</div>
                </div>
              )}

              <div className="pt-2 flex justify-between items-center text-[11px] text-gray-500">
                <a href="/agent/quotes" className="hover:text-purple-700 hover:underline inline-flex items-center gap-1">
                  <ExternalLink className="w-3 h-3" /> Vai al preventivo
                </a>
                <span>Aggiornata {detail.updated_at ? new Date(detail.updated_at).toLocaleString('it-IT') : '—'}</span>
              </div>
            </div>

            <div className="sticky bottom-0 bg-white border-t px-6 py-3 flex justify-end">
              <button onClick={() => setDetail(null)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md text-sm">Chiudi</button>
            </div>
          </div>
        </div>
      )}

      {/* Picker: scegli preventivo + riga */}
      {showPicker && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="gr-picker-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
            <div className="bg-gradient-to-r from-purple-600 to-fuchsia-500 text-white px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <h2 className="text-lg font-semibold flex items-center gap-2"><Plus className="w-5 h-5" /> Nuova bozza grafica</h2>
              <button onClick={() => setShowPicker(false)} className="p-1 hover:bg-white/20 rounded"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4 text-sm">
              {pickerLoading ? (
                <div className="py-8 text-center text-gray-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Caricamento preventivi…</div>
              ) : quotes.length === 0 ? (
                <div className="py-8 text-center text-gray-500">
                  Nessun preventivo attivo. <a href="/agent/quotes/new" className="underline text-purple-700">Crea un preventivo</a> prima.
                </div>
              ) : (
                <>
                  <div>
                    <label className="text-xs font-semibold text-gray-700 uppercase">Preventivo</label>
                    <select value={pickerQuoteId} onChange={(e) => { setPickerQuoteId(e.target.value); setPickerItemIndex(0); }}
                      data-testid="picker-quote-select"
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm">
                      {quotes.map((q) => (
                        <option key={q.id} value={q.id}>
                          #{q.quote_number} · {q.customer_data?.business_name || q.customer_data?.customer_name || 'cliente'} · {q.status}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-700 uppercase">Riga prodotto</label>
                    <div className="mt-1 space-y-1">
                      {(selectedQuote?.items || []).map((it, idx) => {
                        const has = existingIndexes.has(idx);
                        return (
                          <label key={idx}
                            className={`flex items-center gap-2 border rounded-md px-3 py-2 text-sm ${has ? 'bg-gray-50 cursor-not-allowed opacity-60' : pickerItemIndex === idx ? 'border-purple-500 bg-purple-50 cursor-pointer' : 'border-gray-200 hover:bg-purple-50/50 cursor-pointer'}`}>
                            <input type="radio" name="picker-item" checked={pickerItemIndex === idx}
                              disabled={has}
                              onChange={() => setPickerItemIndex(idx)}
                              data-testid={`picker-item-${idx}`} />
                            <div className="flex-1 min-w-0">
                              <div className="text-gray-900 truncate">{it.description || `Riga #${idx + 1}`}</div>
                              <div className="text-[11px] text-gray-500">{it.format} · {it.quantity} pz</div>
                            </div>
                            {has && <span className="text-[10px] text-gray-500 italic">già richiesta</span>}
                          </label>
                        );
                      })}
                      {(selectedQuote?.items || []).length === 0 && (
                        <div className="text-xs text-gray-500 italic">Nessuna riga nel preventivo.</div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className="bg-white border-t px-6 py-3 flex justify-end gap-2 rounded-b-2xl">
              <button onClick={() => setShowPicker(false)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md text-sm">Annulla</button>
              <button onClick={confirmPicker}
                disabled={!selectedQuote || existingIndexes.has(pickerItemIndex) || (selectedQuote?.items || []).length === 0}
                data-testid="btn-picker-confirm"
                className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 text-sm font-medium">
                <ArrowRight className="w-4 h-4" /> Continua
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal creazione richiesta */}
      {newRequestModal && (
        <GraphicRequestModal
          quoteId={newRequestModal.quoteId}
          itemIndex={newRequestModal.itemIndex}
          defaultProductName={newRequestModal.defaultProductName}
          existingRequestId={null}
          agentToken={agentToken}
          onClose={() => setNewRequestModal(null)}
          onSaved={() => { setNewRequestModal(null); load(); }}
        />
      )}
    </AgentLayout>
  );
};

export default AgentGraphicRequests;
